import { ApiFactory } from '../../api/agwasuite-api-factory';
import { ApiTabSession } from '../../api/core/agwasuite-api-tab-session';
import { sessionManager } from '../../services/session-manager';

/**
 * The sign-out for a tab whose sign-in check named somebody other than the person it acts as
 * (D-143, connection-health-acceptance.md rule 4, @CONN-HEALTH-008).
 *
 * Unlike a refused check, the server has NOT ended anything: this tab's session is still live and
 * still holds its locks. So the tab ends that session on the server FIRST, which releases its locks
 * at once, and only then signs out locally. The other order would clear the session id before the
 * one request able to end it was sent, and the session would hold its locks until the lease lapsed.
 *
 * If the server end fails (network, server error), the tab still signs out locally: it must not go
 * on acting as a person the server no longer confirms. The locks are then left to their lease, which
 * is the fallback that bounds them.
 */
export async function signOutAfterMismatch(deps: {
  /** Ends this tab's session on the server as the acting person; resolves false on any failure. */
  endTabSession: () => Promise<boolean>;
  /** The local sign-out every signed-out tab runs (signOutAfterRefusal). */
  signOutLocally: () => void;
}): Promise<void> {
  // Caught as well as checked: the local sign-out must run whatever the server end does, including
  // an implementation that rejects rather than resolving false.
  let ended = false;
  try {
    ended = await deps.endTabSession();
  } catch {
    ended = false;
  }
  if (!ended) {
    console.warn(
      "[connection-health] could not end this tab's session on the server after a sign-in mismatch; its locks are left to their lease."
    );
  }
  deps.signOutLocally();
}

/**
 * Ends this tab's session through `POST /api/TabSession/end`, authenticated as the person the tab
 * ACTS AS, never as whatever account the tab has stored: in a mismatch those differ by definition,
 * and the stored one would authenticate as somebody else, who cannot prove this tab's session. The
 * account key is derived from the acting user id the same way the server derives it
 * (`AgwaAuthenticationOptions.CreateSessionId`), and the CSRF header carries the same user.
 * Never throws: every failure is `false`.
 */
export async function endThisTabSessionAs(
  actingUserId: string | undefined
): Promise<boolean> {
  if (!actingUserId) return false;
  try {
    const api = ApiFactory.authenticated(ApiTabSession, {
      userId: actingUserId,
      accountKey: sessionManager.createAccountKey(actingUserId),
    });
    const response = await api.end();
    return response.success;
  } catch {
    return false;
  }
}
